import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Field } from "@/components/common";

const EMPTY = { email: "", password: "", clinic_name: "", name: "", phone: "", address: "", city: "", piva: "", n_ce: "", codice_fiscale: "", cod_destinatario: "", pec: "", tipo: "veterinario", active: true };

export const VetDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(initial || {}), password: "" }); }, [open, initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!f.clinic_name.trim() || !f.email.trim()) return toast.error("Nome clinica ed email sono obbligatori");
    if (!f.id && f.password.length < 8) return toast.error("Imposta una password di almeno 8 caratteri");
    setBusy(true);
    const payload = { ...f, password: f.password || null };
    try {
      const { data } = f.id ? await api.put(`/admin/vets/${f.id}`, payload) : await api.post("/admin/vets", payload);
      toast.success(f.id ? "Veterinario aggiornato" : "Account veterinario creato");
      onSaved?.(data, f.password);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FAF8F5]" data-testid="vet-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-[#0D3B52]">{f.id ? "Modifica veterinario" : "Nuovo veterinario"}</DialogTitle>
          <DialogDescription>Anagrafica della clinica e credenziali di accesso all'area riservata.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome clinica *" className="sm:col-span-2"><Input data-testid="vet-clinic-input" value={f.clinic_name} onChange={set("clinic_name")} className="bg-white" /></Field>
          <Field label="Referente"><Input data-testid="vet-name-input" value={f.name} onChange={set("name")} className="bg-white" /></Field>
          <Field label="Telefono"><Input data-testid="vet-phone-input" value={f.phone} onChange={set("phone")} className="bg-white" /></Field>
          <Field label="Indirizzo"><Input data-testid="vet-address-input" value={f.address} onChange={set("address")} className="bg-white" /></Field>
          <Field label="Città"><Input data-testid="vet-city-input" value={f.city} onChange={set("city")} className="bg-white" /></Field>
          <Field label="Partita IVA"><Input data-testid="vet-piva-input" value={f.piva} onChange={set("piva")} className="bg-white" /></Field>
          <Field label="N° CE / registrazione"><Input data-testid="vet-nce-input" value={f.n_ce} onChange={set("n_ce")} className="bg-white" /></Field>
          <Field label="Codice fiscale"><Input data-testid="vet-cf-input" value={f.codice_fiscale} onChange={set("codice_fiscale")} className="bg-white" /></Field>
          <Field label="Codice destinatario (SDI)"><Input data-testid="vet-sdi-input" value={f.cod_destinatario} onChange={set("cod_destinatario")} className="bg-white" /></Field>
          <Field label="PEC"><Input data-testid="vet-pec-input" value={f.pec} onChange={set("pec")} className="bg-white" /></Field>
          <Field label="Tipo">
            <select data-testid="vet-tipo-select" value={f.tipo || "veterinario"} onChange={set("tipo")} className="flex h-10 w-full rounded-md border border-[#E2DDD5] bg-white px-3 text-sm">
              <option value="veterinario">Veterinario</option><option value="agenzia">Agenzia esterna</option>
            </select>
          </Field>
          <Field label="Email di accesso *"><Input data-testid="vet-email-input" type="email" value={f.email} onChange={set("email")} className="bg-white" /></Field>
          <Field label={f.id ? "Nuova password (opzionale)" : "Password *"} hint="Minimo 8 caratteri">
            <Input data-testid="vet-password-input" type="text" value={f.password} onChange={set("password")} className="bg-white" />
          </Field>
          <label className="flex items-center gap-3 text-sm text-[#1C2A33] sm:col-span-2">
            <Switch checked={f.active} onCheckedChange={(v) => setF((p) => ({ ...p, active: v }))} data-testid="vet-active-switch" /> Account attivo
          </label>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="vet-save-button" className="rounded-full bg-[#0D3B52] hover:bg-[#082535] px-8">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
